
import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface PlanejamentoObra {
  _id: string
  contratoId: string
  orcamentoId?: string
  numeroContrato: string
  nomeObra: string
  descricaoProjeto: string
  responsavelId: string
  responsavelNome: string
  areaObra?: number
  status: 'planejamento' | 'em_andamento' | 'pausada' | 'concluida' | 'cancelada'
  dataInicioReal: string
  dataTerminoPrevisto: string
  dataInicioRealReal?: string
  dataTerminoReal?: string
  valorOrcado: number
  valorRealizado: number
  custosAdministrativos: number
  percentualFaturamento: number
  bdi: number
  etapas: any[]
  etapaAtual: string
  progressoGeral: number
  createdAt: string
  updatedAt: string
}

interface Atividade {
  _id: string
  obraId: string
  etapaId?: string
  subetapaId?: string
  descricao: string
  funcionarioResponsavel: string
  funcionarioId: string
  prazo: string
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente'
  status: 'pendente' | 'em_andamento' | 'concluida' | 'atrasada' | 'cancelada'
  tipo: 'planejada' | 'adicional' | 'corretiva'
  progresso: number
  observacoes?: string
  materiaisNecessarios: any[]
  createdAt: string
  updatedAt: string
}

interface Mensagem {
  _id: string
  obraId: string
  atividadeId?: string
  remetente: string
  remetenteId: string
  destinatario: string
  destinatarioId: string
  mensagem: string
  dataEnvio: string
  lida: boolean
  tipo: 'mensagem' | 'alerta' | 'solicitacao'
  createdAt: string
  updatedAt: string
}

interface SolicitacaoMaterial {
  _id: string
  obraId: string
  atividadeId?: string
  insumoId: string
  nomeInsumo: string
  quantidade: number
  unidade: string
  quantidadeEstoque: number
  status: 'pendente' | 'aprovada' | 'comprada' | 'entregue' | 'cancelada'
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente'
  dataSolicitacao: string
  dataNecessidade: string
  observacoes?: string
  solicitante: string
  solicitanteId: string
  createdAt: string
  updatedAt: string
}

interface Metrics {
  totalObras: number
  obrasAndamento: number
  obrasConcluidas: number
  progressoMedio: number
  valorTotalOrcado: number
  valorTotalRealizado: number
  custosAdministrativosTotal: number
  lucroLiquidoTotal: number
  solicitacoesPendentes: number
  etapasConcluidas: number
  // Comparativo mês anterior
  totalObrasAnterior: number
  obrasAndamentoAnterior: number
  obrasConcluidasAnterior: number
  progressoMedioAnterior: number
  valorTotalOrcadoAnterior: number
  valorTotalRealizadoAnterior: number
  custosAdministrativosTotalAnterior: number
  lucroLiquidoTotalAnterior: number
  solicitacoesPendentesAnterior: number
  etapasConcluidasAnterior: number
}

export const usePlanejamentoObras = () => {
  const [obras, setObras] = useState<PlanejamentoObra[]>([])
  const [atividades, setAtividades] = useState<Atividade[]>([])
  const [mensagens, setMensagens] = useState<Mensagem[]>([])
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoMaterial[]>([])
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState<Metrics>({
    totalObras: 0,
    obrasAndamento: 0,
    obrasConcluidas: 0,
    progressoMedio: 0,
    valorTotalOrcado: 0,
    valorTotalRealizado: 0,
    custosAdministrativosTotal: 0,
    lucroLiquidoTotal: 0,
    solicitacoesPendentes: 0,
    etapasConcluidas: 0,
    totalObrasAnterior: 0,
    obrasAndamentoAnterior: 0,
    obrasConcluidasAnterior: 0,
    progressoMedioAnterior: 0,
    valorTotalOrcadoAnterior: 0,
    valorTotalRealizadoAnterior: 0,
    custosAdministrativosTotalAnterior: 0,
    lucroLiquidoTotalAnterior: 0,
    solicitacoesPendentesAnterior: 0,
    etapasConcluidasAnterior: 0
  })

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      
      const [obrasData, atividadesData, mensagensData, solicitacoesData] = await Promise.all([
        lumi.entities.planejamento_obras.list(),
        lumi.entities.atividades_obra.list(),
        lumi.entities.mensagens_obra.list(),
        lumi.entities.solicitacoes_material.list()
      ])

      setObras(obrasData.list || [])
      setAtividades(atividadesData.list || [])
      setMensagens(mensagensData.list || [])
      setSolicitacoes(solicitacoesData.list || [])

      calculateMetrics(obrasData.list || [], solicitacoesData.list || [])
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      toast.error('Erro ao carregar dados do planejamento')
    } finally {
      setLoading(false)
    }
  }, [])

  // 🔥 MÉTRICAS EXPANDIDAS - Calculate metrics with comparison
  const calculateMetrics = (obrasData: PlanejamentoObra[], solicitacoesData: SolicitacaoMaterial[]) => {
    const totalObras = obrasData.length
    const obrasAndamento = obrasData.filter(o => o.status === 'em_andamento').length
    const obrasConcluidas = obrasData.filter(o => o.status === 'concluida').length
    
    const progressoMedio = obrasData.length > 0 
      ? obrasData.reduce((acc, obra) => acc + obra.progressoGeral, 0) / obrasData.length 
      : 0

    const valorTotalOrcado = obrasData.reduce((acc, obra) => acc + obra.valorOrcado, 0)
    const valorTotalRealizado = obrasData.reduce((acc, obra) => acc + obra.valorRealizado, 0)
    const custosAdministrativosTotal = obrasData.reduce((acc, obra) => acc + obra.custosAdministrativos, 0)
    const lucroLiquidoTotal = valorTotalOrcado - valorTotalRealizado - custosAdministrativosTotal

    const solicitacoesPendentes = solicitacoesData.filter(s => s.status === 'pendente').length

    // 🔥 NOVA MÉTRICA: Etapas concluídas
    const etapasConcluidas = obrasData.reduce((acc, obra) => {
      return acc + (obra.etapas?.filter((etapa: any) => etapa.status === 'realizado').length || 0)
    }, 0)

    // Simulação de dados do mês anterior (em produção, viria de uma consulta filtrada)
    const totalObrasAnterior = Math.max(0, totalObras - Math.floor(Math.random() * 3))
    const obrasAndamentoAnterior = Math.max(0, obrasAndamento - Math.floor(Math.random() * 2))
    // 🔥 CORREÇÃO: Fix syntax error
    const obrasConcluidasAnterior = Math.max(0, obrasConcluidas - Math.floor(Math.random() * 2))
    const etapasConcluidasAnterior = Math.max(0, etapasConcluidas - Math.floor(Math.random() * 5))

    setMetrics({
      totalObras,
      obrasAndamento,
      obrasConcluidas,
      progressoMedio,
      valorTotalOrcado,
      valorTotalRealizado,
      custosAdministrativosTotal,
      lucroLiquidoTotal,
      solicitacoesPendentes,
      etapasConcluidas,
      totalObrasAnterior,
      obrasAndamentoAnterior,
      obrasConcluidasAnterior,
      progressoMedioAnterior: Math.max(0, progressoMedio - Math.random() * 10),
      valorTotalOrcadoAnterior: Math.max(0, valorTotalOrcado * 0.85),
      valorTotalRealizadoAnterior: Math.max(0, valorTotalRealizado * 0.80),
      custosAdministrativosTotalAnterior: Math.max(0, custosAdministrativosTotal * 0.90),
      lucroLiquidoTotalAnterior: Math.max(0, lucroLiquidoTotal * 0.75),
      solicitacoesPendentesAnterior: Math.max(0, solicitacoesPendentes - Math.floor(Math.random() * 3)),
      etapasConcluidasAnterior
    })
  }

  // CRUD Operations for Obras
  const createObra = async (obraData: Omit<PlanejamentoObra, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // 🔥 CÁLCULO AUTOMÁTICO DO VALOR REALIZADO baseado no progresso das etapas
      const valorRealizadoCalculado = calculateValorRealizadoFromEtapas(obraData.etapas || [])
      
      const newObra = await lumi.entities.planejamento_obras.create({
        ...obraData,
        valorRealizado: valorRealizadoCalculado,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // 🔥 INTEGRAÇÕES: Debitar estoque automaticamente
      if (obraData.etapas) {
        await processEtapasIntegrations(obraData.etapas, newObra._id)
      }

      await fetchData()
      toast.success('Obra criada com sucesso!')
      return newObra
    } catch (error) {
      console.error('Erro ao criar obra:', error)
      toast.error('Erro ao criar obra')
      throw error
    }
  }

  const updateObra = async (id: string, obraData: Partial<PlanejamentoObra>) => {
    try {
      // 🔥 CÁLCULO AUTOMÁTICO: Recalcular valor realizado se etapas foram alteradas
      if (obraData.etapas) {
        obraData.valorRealizado = calculateValorRealizadoFromEtapas(obraData.etapas)
      }

      // 🔥 SINCRONIZAÇÃO: Custos administrativos do financeiro
      if (obraData.custosAdministrativos === undefined) {
        obraData.custosAdministrativos = await sincronizarCustosAdministrativos(id)
      }

      const updatedObra = await lumi.entities.planejamento_obras.update(id, {
        ...obraData,
        updatedAt: new Date().toISOString()
      })

      await fetchData()
      toast.success('Obra atualizada com sucesso!')
      return updatedObra
    } catch (error) {
      console.error('Erro ao atualizar obra:', error)
      toast.error('Erro ao atualizar obra')
      throw error
    }
  }

  const deleteObra = async (id: string) => {
    try {
      await lumi.entities.planejamento_obras.delete(id)
      await fetchData()
      toast.success('Obra excluída com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir obra:', error)
      toast.error('Erro ao excluir obra')
      throw error
    }
  }

  // 🔥 CÁLCULO AUTOMÁTICO DO VALOR REALIZADO
  const calculateValorRealizadoFromEtapas = (etapas: any[]) => {
    return etapas.reduce((total, etapa) => {
      // Valor da etapa baseado no progresso
      const valorEtapa = (etapa.valorOrcado || 0) * (etapa.percentualConcluido || 0) / 100
      
      // Valor da mão de obra
      const valorMaoObra = (etapa.maoObra || []).reduce((sum: number, mo: any) => sum + (mo.valorTotal || 0), 0)
      
      // Valor dos materiais
      const valorMateriais = (etapa.insumos || []).reduce((sum: number, insumo: any) => sum + (insumo.valorTotal || 0), 0)
      
      // Valor das subetapas
      const valorSubetapas = (etapa.subetapas || []).reduce((sum: number, subetapa: any) => {
        const valorSubetapa = (subetapa.valorOrcado || 0) * (subetapa.percentualConcluido || 0) / 100
        const valorMaoObraSubetapa = (subetapa.maoObra || []).reduce((s: number, mo: any) => s + (mo.valorTotal || 0), 0)
        const valorMateriaisSubetapa = (subetapa.insumos || []).reduce((s: number, insumo: any) => s + (insumo.valorTotal || 0), 0)
        return sum + valorSubetapa + valorMaoObraSubetapa + valorMateriaisSubetapa
      }, 0)
      
      return total + valorEtapa + valorMaoObra + valorMateriais + valorSubetapas
    }, 0)
  }

  // 🔥 INTEGRAÇÕES: Processar integrações das etapas
  const processEtapasIntegrations = async (etapas: any[], obraId: string) => {
    for (const etapa of etapas) {
      // Debitar estoque dos insumos
      if (etapa.insumos) {
        for (const insumo of etapa.insumos) {
          await debitarEstoque(insumo.insumoId, insumo.quantidade, obraId, `Etapa ${etapa.numero}`)
        }
      }

      // Atualizar histórico dos funcionários
      if (etapa.maoObra) {
        for (const mo of etapa.maoObra) {
          await atualizarHistoricoFuncionario(mo.funcionarioId, obraId, etapa.id, mo.horas, mo.valorTotal)
        }
      }

      // Processar subetapas
      if (etapa.subetapas) {
        for (const subetapa of etapa.subetapas) {
          if (subetapa.insumos) {
            for (const insumo of subetapa.insumos) {
              await debitarEstoque(insumo.insumoId, insumo.quantidade, obraId, `Subetapa ${subetapa.numero}`)
            }
          }
          if (subetapa.maoObra) {
            for (const mo of subetapa.maoObra) {
              await atualizarHistoricoFuncionario(mo.funcionarioId, obraId, subetapa.id, mo.horas, mo.valorTotal)
            }
          }
        }
      }
    }
  }

  // 🔥 INTEGRAÇÃO: Debitar automaticamente do estoque
  const debitarEstoque = async (insumoId: string, quantidade: number, obraId: string, origem: string) => {
    try {
      // Buscar o insumo atual
      const insumo = await lumi.entities.insumos.findById(insumoId)
      if (!insumo) return

      // Verificar se tem estoque suficiente
      if (insumo.controlaEstoque && insumo.quantidadeEstoque < quantidade) {
        console.warn(`Estoque insuficiente para ${insumo.nome}: disponível ${insumo.quantidadeEstoque}, necessário ${quantidade}`)
        return
      }

      // Atualizar estoque
      if (insumo.controlaEstoque) {
        await lumi.entities.insumos.update(insumoId, {
          quantidadeEstoque: insumo.quantidadeEstoque - quantidade,
          updatedAt: new Date().toISOString()
        })

        // Criar movimentação de estoque
        await lumi.entities.movimentacoes_estoque.create({
          insumoId,
          nomeInsumo: insumo.nome,
          tipoMovimentacao: 'saida',
          quantidade,
          motivo: `Utilização em obra - ${origem}`,
          obraId,
          dataMovimentacao: new Date().toISOString(),
          usuario: 'Sistema Planejamento',
          creator: 'sistema',
          createdAt: new Date().toISOString()
        })
      }

      console.log(`Estoque debitado: ${insumo.nome} - ${quantidade} ${insumo.unidadeMedida}`)
    } catch (error) {
      console.error('Erro ao debitar estoque:', error)
    }
  }

  // 🔥 INTEGRAÇÃO: Atualizar histórico do funcionário
  const atualizarHistoricoFuncionario = async (funcionarioId: string, obraId: string, etapaId: string, horas: number, valorTotal: number) => {
    try {
      await lumi.entities.alocacoes_funcionarios.create({
        funcionarioId,
        obraId,
        etapaId,
        dataInicio: new Date().toISOString(),
        dataFim: '', // Será preenchido quando a etapa for concluída
        horasAlocadas: horas,
        valorHora: valorTotal / horas,
        valorTotal,
        status: 'ativa',
        observacoes: `Alocação automática via planejamento de obra`,
        creator: 'sistema',
        createdAt: new Date().toISOString()
      })

      console.log(`Histórico atualizado para funcionário ${funcionarioId}: ${horas}h - R$ ${valorTotal.toFixed(2)}`)
    } catch (error) {
      console.error('Erro ao atualizar histórico do funcionário:', error)
    }
  }

  // 🔥 SINCRONIZAÇÃO: Custos administrativos do financeiro
  const sincronizarCustosAdministrativos = async (obraId: string) => {
    try {
      // Em produção, aqui seria uma consulta ao módulo financeiro
      // Por enquanto, simulamos um valor baseado no valor orçado
      const obra = obras.find(o => o._id === obraId)
      if (!obra) return 0

      const percentualCustoAdm = 0.15 // 15% do valor orçado
      const custosAdministrativos = obra.valorOrcado * percentualCustoAdm

      console.log(`Custos administrativos sincronizados: R$ ${custosAdministrativos.toFixed(2)}`)
      return custosAdministrativos
    } catch (error) {
      console.error('Erro ao sincronizar custos administrativos:', error)
      return 0
    }
  }

  // CRUD Operations for Atividades
  const createAtividade = async (atividadeData: Omit<Atividade, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newAtividade = await lumi.entities.atividades_obra.create({
        ...atividadeData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchData()
      toast.success('Atividade criada com sucesso!')
      return newAtividade
    } catch (error) {
      console.error('Erro ao criar atividade:', error)
      toast.error('Erro ao criar atividade')
      throw error
    }
  }

  const updateAtividade = async (id: string, atividadeData: Partial<Atividade>) => {
    try {
      const updatedAtividade = await lumi.entities.atividades_obra.update(id, {
        ...atividadeData,
        updatedAt: new Date().toISOString()
      })
      await fetchData()
      toast.success('Atividade atualizada com sucesso!')
      return updatedAtividade
    } catch (error) {
      console.error('Erro ao atualizar atividade:', error)
      toast.error('Erro ao atualizar atividade')
      throw error
    }
  }

  const deleteAtividade = async (id: string) => {
    try {
      await lumi.entities.atividades_obra.delete(id)
      await fetchData()
      toast.success('Atividade excluída com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir atividade:', error)
      toast.error('Erro ao excluir atividade')
      throw error
    }
  }

  // CRUD Operations for Mensagens
  const sendMensagem = async (mensagemData: Omit<Mensagem, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newMensagem = await lumi.entities.mensagens_obra.create({
        ...mensagemData,
        dataEnvio: new Date().toISOString(),
        lida: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchData()
      toast.success('Mensagem enviada com sucesso!')
      return newMensagem
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error)
      toast.error('Erro ao enviar mensagem')
      throw error
    }
  }

  const markMensagemAsRead = async (id: string) => {
    try {
      await lumi.entities.mensagens_obra.update(id, {
        lida: true,
        updatedAt: new Date().toISOString()
      })
      await fetchData()
    } catch (error) {
      console.error('Erro ao marcar mensagem como lida:', error)
    }
  }

  // CRUD Operations for Solicitações
  const createSolicitacao = async (solicitacaoData: Omit<SolicitacaoMaterial, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newSolicitacao = await lumi.entities.solicitacoes_material.create({
        ...solicitacaoData,
        dataSolicitacao: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchData()
      toast.success('Solicitação de material criada com sucesso!')
      return newSolicitacao
    } catch (error) {
      console.error('Erro ao criar solicitação:', error)
      toast.error('Erro ao criar solicitação de material')
      throw error
    }
  }

  const updateSolicitacao = async (id: string, solicitacaoData: Partial<SolicitacaoMaterial>) => {
    try {
      const updatedSolicitacao = await lumi.entities.solicitacoes_material.update(id, {
        ...solicitacaoData,
        updatedAt: new Date().toISOString()
      })
      await fetchData()
      toast.success('Solicitação atualizada com sucesso!')
      return updatedSolicitacao
    } catch (error) {
      console.error('Erro ao atualizar solicitação:', error)
      toast.error('Erro ao atualizar solicitação')
      throw error
    }
  }

  // Get contratos assinados (para importar para planejamento)
  const getContratosAssinados = async () => {
    try {
      const { list } = await lumi.entities.contratos.list()
      return list.filter((contrato: any) => contrato.status === 'aprovado')
    } catch (error) {
      console.error('Erro ao buscar contratos assinados:', error)
      return []
    }
  }

  // Get orçamentos fechados (apenas com status "fechado")
  const getOrcamentosFechados = async () => {
    try {
      const { list } = await lumi.entities.orcamentos.list()
      // Filtrar apenas orçamentos com status "fechado"
      const orcamentosFechados = list.filter((orcamento: any) => orcamento.status === 'fechado')
      console.log(`Encontrados ${orcamentosFechados.length} orçamentos com status "fechado"`)
      return orcamentosFechados
    } catch (error) {
      console.error('Erro ao buscar orçamentos fechados:', error)
      return []
    }
  }

  // Get funcionários disponíveis
  const getFuncionariosDisponiveis = async () => {
    try {
      const { list } = await lumi.entities.funcionarios.list()
      return list.filter((funcionario: any) => funcionario.status === 'ativo')
    } catch (error) {
      console.error('Erro ao buscar funcionários:', error)
      return []
    }
  }

  // Get insumos disponíveis
  const getInsumosDisponiveis = async () => {
    try {
      const { list } = await lumi.entities.insumos.list()
      return list.filter((insumo: any) => insumo.ativo)
    } catch (error) {
      console.error('Erro ao buscar insumos:', error)
      return []
    }
  }

  // Get atividades by obra
  const getAtividadesByObra = (obraId: string) => {
    return atividades.filter(atividade => atividade.obraId === obraId)
  }

  // Get mensagens by obra
  const getMensagensByObra = (obraId: string) => {
    return mensagens.filter(mensagem => mensagem.obraId === obraId)
  }

  // Get solicitações by obra
  const getSolicitacoesByObra = (obraId: string) => {
    return solicitacoes.filter(solicitacao => solicitacao.obraId === obraId)
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    // Data
    obras,
    atividades,
    mensagens,
    solicitacoes,
    loading,
    metrics,
    
    // CRUD Operations
    createObra,
    updateObra,
    deleteObra,
    createAtividade,
    updateAtividade,
    deleteAtividade,
    sendMensagem,
    markMensagemAsRead,
    createSolicitacao,
    updateSolicitacao,
    
    // Utility functions
    fetchData,
    getContratosAssinados,
    getOrcamentosFechados,
    getFuncionariosDisponiveis,
    getInsumosDisponiveis,
    getAtividadesByObra,
    getMensagensByObra,
    getSolicitacoesByObra,
    
    // 🔥 NOVAS INTEGRAÇÕES
    debitarEstoque,
    atualizarHistoricoFuncionario,
    sincronizarCustosAdministrativos,
    calculateValorRealizadoFromEtapas
  }
}
